import React, { useState } from 'react';
import { fmtMoney, statusDef, normalizeSalesChannel, formatOrderDate } from '../../utils/orderUtils';
import { useToast } from '../ToastManager';
import EntityInspector from '../ui/EntityInspector';
import StatusBadge from '../ui/StatusBadge';
import ChannelBadge from '../ui/ChannelBadge';
import Button from '../ui/Button';
import { OrderItems, customerName } from './OrderListInspector';
import { propsSoloLectura } from '../../utils/soloLectura';
import '../ShippedRow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/* ── Drawer de detalle — "Ya enviados" (F5 Tema 4) ───────────────────────────
   Lectura del envío ya despachado + corrección. La misma lógica de antes
   (PUT /api/orders/:id/shipping-edit), re-alojada en el drawer del molde: se
   abre en LECTURA y conmuta a EDICIÓN con "Editar envío" al pie (una sola
   superficie, paralela al drawer de despacho). */

function formatAddressFull(order) {
  const shipping = order?.shipping || {};
  const billing = order?.billing || {};
  const parts = [
    shipping.address_1 || billing.address_1,
    shipping.address_2 || billing.address_2,
    [shipping.city || billing.city, shipping.state || billing.state].filter(Boolean).join(', '),
  ].filter(Boolean);
  return parts.join(' — ') || 'Sin dirección registrada';
}

const PAGO_ENVIO_LABEL = {
  efectivo:      'Efectivo',
  transferencia: 'Transferencia',
  pendiente:     'Pendiente de pago',
};

const EMPTY_EDIT = {
  tipo_transporte: '',
  nombre_transportista: '',
  costo_envio_real: '',
  pago_envio_metodo: '',
  comentario_envio: '',
};

/* order === null → cerrado. Montado con `key={order.id}` en el padre: navegar
   con ↑/↓ remonta y sale de modo edición. */
export default function ShippedDetailDrawer({ order, onClose, onPrev, onNext, onEdited, soloLectura = false }) {
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);

  if (!order) return <EntityInspector open={false} onClose={onClose} />;

  const fullAddress = formatAddressFull(order);
  const cost = order.costo_envio_real;
  const charged = order.envio_cobrado_cliente;
  const diff = (typeof charged === 'number' && typeof cost === 'number') ? charged - cost : null;

  const openEdit = () => {
    setForm({
      tipo_transporte:      order.tipo_transporte || '',
      nombre_transportista: order.nombre_transportista || '',
      costo_envio_real:     order.costo_envio_real != null ? String(order.costo_envio_real) : '',
      pago_envio_metodo:    order.pago_envio_metodo || '',
      comentario_envio:     order.comentario_envio || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    const costoRaw = parseFloat(form.costo_envio_real);
    if (!['mensajero', 'empresa'].includes(form.tipo_transporte)) {
      showToast({ message: 'Selecciona el tipo de transporte.', variant: 'error' });
      return;
    }
    if (!form.nombre_transportista.trim()) {
      showToast({ message: 'Ingresa el nombre del transportista.', variant: 'error' });
      return;
    }
    if (!Number.isFinite(costoRaw) || costoRaw < 0) {
      showToast({ message: 'El costo real debe ser un número mayor o igual a 0.', variant: 'error' });
      return;
    }
    if (!['efectivo', 'transferencia', 'pendiente'].includes(form.pago_envio_metodo)) {
      showToast({ message: 'Selecciona la forma de pago del envío.', variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/shipping-edit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo_transporte:      form.tipo_transporte,
          nombre_transportista: form.nombre_transportista.trim(),
          costo_envio_real:     costoRaw,
          pago_envio_metodo:    form.pago_envio_metodo,
          comentario_envio:     form.comentario_envio.trim(),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al guardar los cambios.');
      }
      showToast({ message: 'Envío actualizado.', variant: 'success' });
      setIsEditing(false);
      if (onEdited) onEdited();
    } catch (err) {
      showToast({ message: err.message, variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  return (
    <EntityInspector
      open
      mode="drawer"
      title={`Envío del pedido #${order.number}`}
      subtitle={`${order.fecha_envio || formatOrderDate(order.date_created)} · ${customerName(order)}`}
      status={<>
        <StatusBadge variant={statusDef(order.status).variant}>{statusDef(order.status).label}</StatusBadge>
        <ChannelBadge channel={normalizeSalesChannel(order).key} />
      </>}
      actions={isEditing ? (
        <>
          <Button variant="tertiary" onClick={() => setIsEditing(false)} disabled={saving}>Cancelar</Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </>
      ) : (
        <Button variant="primary" {...propsSoloLectura(soloLectura)} onClick={openEdit}>Editar envío</Button>
      )}
      onClose={onClose}
      onPrev={isEditing ? null : onPrev}
      onNext={isEditing ? null : onNext}
    >
      <div className="op-inspector-body">
        <OrderItems order={order} />
        {isEditing ? (
          <div className="shr-edit-form">
            <div className="shr-edit-row">
              <label className="shr-edit-label">Tipo de transporte</label>
              <select className="shr-edit-select" {...field('tipo_transporte')}>
                <option value="">Seleccionar…</option>
                <option value="mensajero">Mensajero</option>
                <option value="empresa">Empresa de transporte</option>
              </select>
            </div>

            <div className="shr-edit-row">
              <label className="shr-edit-label">Nombre del transportista</label>
              <input type="text" className="shr-edit-input" placeholder="Ej. Interrapidísimo, Juan Pérez…"
                {...field('nombre_transportista')} />
            </div>

            <div className="shr-edit-row">
              <label className="shr-edit-label">Costo real del envío (COP)</label>
              <input type="number" className="shr-edit-input" min="0" step="100"
                placeholder="0" {...field('costo_envio_real')} />
            </div>

            <div className="shr-edit-row">
              <label className="shr-edit-label">Forma de pago</label>
              <select className="shr-edit-select" {...field('pago_envio_metodo')}>
                <option value="">Seleccionar…</option>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="pendiente">Pendiente (cuenta por pagar)</option>
              </select>
            </div>

            <div className="shr-edit-row">
              <label className="shr-edit-label">Comentario (opcional)</label>
              <textarea className="shr-edit-textarea" rows={2} placeholder="Notas del envío…"
                {...field('comentario_envio')} />
            </div>
          </div>
        ) : (
          <div className="shr-grid">
            <div className="shr-detail">
              <span>Fecha de envío</span>
              <strong>{order.fecha_envio || '—'}</strong>
            </div>
            <div className="shr-detail">
              <span>Transporte</span>
              <strong>{order.tipo_transporte === 'empresa' ? 'Empresa' : 'Mensajero'}{order.nombre_transportista ? ` · ${order.nombre_transportista}` : ''}</strong>
            </div>
            <div className="shr-detail">
              <span>Forma de pago envío</span>
              <strong>{PAGO_ENVIO_LABEL[order.pago_envio_metodo] || order.pago_envio_metodo || '—'}</strong>
            </div>
            <div className="shr-detail">
              <span>Gestionó</span>
              <strong>{order.despachado_por || '—'}</strong>
            </div>
            <div className="shr-detail">
              <span>Total pedido</span>
              <strong>{fmtMoney(order.total)}</strong>
            </div>
            <div className="shr-detail">
              <span>Cobrado por envío</span>
              <strong>{charged != null ? fmtMoney(charged) : '—'}</strong>
            </div>
            <div className="shr-detail">
              <span>Costo real</span>
              <strong>{cost != null ? fmtMoney(cost) : '—'}</strong>
            </div>
            <div className="shr-detail">
              <span>Diferencia</span>
              <strong className={diff == null ? '' : diff > 0 ? 'shr-diff-gain' : diff < 0 ? 'shr-diff-loss' : ''}>
                {diff == null
                  ? '—'
                  : diff === 0
                    ? '$0'
                    : diff > 0
                      ? `${fmtMoney(Math.abs(diff))} a favor`
                      : `${fmtMoney(Math.abs(diff))} asumida`}
              </strong>
            </div>
            <div className="shr-detail shr-detail--wide">
              <span>Dirección completa</span>
              <strong>{fullAddress}</strong>
            </div>
            {order.billing?.phone && (
              <div className="shr-detail">
                <span>Teléfono</span>
                <strong>{order.billing.phone}</strong>
              </div>
            )}
            {order.comentario_envio && (
              <div className="shr-detail shr-detail--wide">
                <span>Comentario</span>
                <strong>{order.comentario_envio}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </EntityInspector>
  );
}
